import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchProductById, fetchHighestBid, updateProduct } from '../../lib/api';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await fetchProductById(id);
        const highest = await fetchHighestBid(id);
        const normalized = {
          id: p?.id,
          name: p?.product_name || p?.name || '',
          image: p?.primary_image_url || p?.image_url || '',
          price: p?.price ?? p?.initial_price ?? 0,
          oldPrice: p?.mrp ?? 0,
          discount: 0,
          rating: 0,
          reviews: 0,
          wishlist: false,
          currentBid: highest ?? 0,
          minBid: (highest ?? 0) + 1,
          details: {
            material: p?.material || '',
            weight: p?.weight || '',
            dimensions: p?.dimensions || '',
            warranty: p?.warranty || '',
            skinSafe: '',
            cod: '',
          },
          offers: [],
          inStock: (p?.stock ?? p?.quantity ?? 0) > 0,
          description: p?.description || '',
          features: [],
        };
        setProduct(normalized);
        setEditData(normalized);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();
  }, [id]);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleSave = async () => {
    try {
      const payload = {
        product_name: editData.name,
        name: editData.name,
        price: Number(editData.price) || 0,
        initial_price: Number(editData.price) || 0,
        mrp: Number(editData.oldPrice) || null,
        description: editData.description,
        stock: editData.inStock ? (product?.stock ?? 1) : 0,
        quantity: editData.inStock ? (product?.stock ?? 1) : 0,
      };
      await updateProduct(product.id, payload);
      setProduct(editData);
      setShowEdit(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-[#faf8f3] min-h-screen">
      {!product ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row gap-8 bg-white rounded-xl shadow p-6">
            {/* Product Image */}
            <div className="flex-1 flex items-center justify-center">
              <img src={product.image} alt={product.name} className="rounded-xl w-72 h-72 object-contain bg-[#f6f1e7]" />
            </div>
        {/* Product Info */}
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex items-start justify-between">
            <h2 className="text-2xl font-bold font-serif text-[#2d2d2d]">{product.name}</h2>
            <button className="text-gray-400 text-2xl">♡</button>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-2xl font-bold text-[#2d2d2d]">₹{product.price.toLocaleString()}</span>
            <span className="line-through text-gray-400 text-lg">₹{product.oldPrice.toLocaleString()}</span>
            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">SAVE {product.discount}%</span>
          </div>
          <div className="flex items-center gap-2 text-yellow-600 mt-1">
            <span>⭐ {product.rating}</span>
            <span className="text-gray-400">({product.reviews})</span>
          </div>
          {/* Bidding Section */}
          <div className="bg-[#f7f3ea] rounded-lg p-4 mt-4">
            <div className="font-semibold mb-2">Current Highest Bid: <span className="text-[#bfa14a] font-bold">₹{product.currentBid}</span></div>
            <div className="flex gap-2 mb-2">
              <input type="number" min={product.minBid} placeholder="Enter your bid" className="border rounded px-3 py-2 flex-1" />
              <button className="bg-[#bfa14a] text-white px-4 py-2 rounded font-semibold">Place Bid</button>
            </div>
            <div className="flex gap-2">
              <button className="bg-blue-600 text-white px-4 py-2 rounded font-semibold flex-1">Notify Me</button>
            </div>
            <div className="text-xs text-gray-500 mt-2">Minimum bid: ₹{product.minBid}</div>
          </div>
          {/* Offers */}
          <div className="mt-4">
            {product.offers && product.offers.map((offer, i) => (
              <div key={i} className="bg-green-50 text-green-700 rounded px-3 py-1 text-sm mb-1">{offer}</div>
            ))}
          </div>
          {/* Stock Status */}
          <div className="mt-2 text-green-700 font-semibold">{product.inStock ? 'In stock - ready to ship' : 'Out of stock'}</div>
          {/* Description */}
          <div className="mt-2 text-gray-700">{product.description}</div>
          {/* Edit Button */}
          <button className="mt-4 bg-blue-500 text-white px-4 py-2 rounded font-semibold w-max" onClick={() => setShowEdit(true)}>Edit</button>
        </div>
      </div>
      {/* Product Details & Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-bold mb-2">Product Details</h3>
          <div className="text-sm text-gray-700">
            <div><b>Material:</b> {product.details.material}</div>
            <div><b>Weight:</b> {product.details.weight}</div>
            <div><b>Dimensions:</b> {product.details.dimensions}</div>
            <div><b>Warranty:</b> {product.details.warranty}</div>
            <div><b>Skin Safe:</b> {product.details.skinSafe}</div>
            <div><b>COD:</b> {product.details.cod}</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center justify-center gap-2">
          {product.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-lg font-semibold">
              <span>{f.icon}</span> {f.label}
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-bold mb-2">How Bidding Works</h3>
          <ul className="text-sm text-gray-700 list-disc pl-5">
            <li>Enter your bid above the current highest bid.</li>
            <li>The highest bid wins when the auction ends.</li>
            <li>All bids are binding. Please bid responsibly.</li>
            <li>Winners will be notified by email.</li>
          </ul>
        </div>
      </div>
      {/* Share Button */}
      <div className="flex justify-center mt-8">
        <button className="bg-green-500 text-white px-6 py-3 rounded-full font-bold text-lg flex items-center gap-2">
          <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.031-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.941 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.151-.174.2-.298.3-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51-.173-.007-.372-.009-.571-.009-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.099 3.205 5.077 4.372.71.306 1.263.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347z"/></svg>
          Share on WhatsApp
        </button>
      </div>
      {/* Edit Modal */}
      {showEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Edit Product</h2>
            <div className="flex flex-col gap-3">
              <label>Name <input className="border rounded px-2 py-1 w-full" name="name" value={editData.name} onChange={handleEditChange} /></label>
              <label>Price <input className="border rounded px-2 py-1 w-full" name="price" value={editData.price} onChange={handleEditChange} /></label>
              <label>Old Price <input className="border rounded px-2 py-1 w-full" name="oldPrice" value={editData.oldPrice} onChange={handleEditChange} /></label>
              <label>Description <textarea className="border rounded px-2 py-1 w-full" name="description" value={editData.description} onChange={handleEditChange} /></label>
              {/* Add more fields as needed */}
            </div>
            <div className="flex gap-2 mt-4">
              <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleSave}>Save</button>
              <button className="bg-gray-300 px-4 py-2 rounded" onClick={() => setShowEdit(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
} 